// src/hooks/useSituation.js
// Situation-aware mode engine.
// Returns situationLevel: 'normal' | 'watch' | 'warning' | 'recovery'
// Used to reconfigure nav, home screen, and resource visibility.

import { useMemo } from 'react';
import { useAlertStore } from '@store/alertStore';

const WATCH_EVENTS = [
  'Hurricane Watch','Tropical Storm Watch','Storm Surge Watch',
  'Tornado Watch','Flash Flood Watch','High Wind Watch','Flood Watch',
  'Extreme Heat Watch','Heat Advisory','Excessive Heat Warning','Excessive Heat Watch',
];
const WARNING_EVENTS = [
  'Hurricane Warning','Tropical Storm Warning','Storm Surge Warning',
  'Tornado Warning','Flash Flood Warning','Flash Flood Emergency',
  'Evacuation Immediate','Extreme Wind Warning',
];

// Maps NWS event to which tools to surface
export const SITUATION_TOOLS = {
  normal: {
    label: 'Normal',
    color: '#059669',
    emoji: '🟢',
    primary: ['neighborhood','feed','news'],
    secondary: ['map','evac','gas','shelters'],
    hidden: [],
  },
  watch: {
    label: 'Storm Watch',
    color: '#d97706',
    emoji: '🟡',
    primary: ['neighborhood','news','evac','map'],
    secondary: ['feed','gas','shelters','radio','gobag'],
    hidden: [],
    banner: 'Storm Watch Active — Preparedness tools prioritized.',
  },
  warning: {
    label: 'Emergency',
    color: '#dc2626',
    emoji: '🔴',
    primary: ['map','evac','shelters','news'],
    secondary: ['neighborhood','radio','contacts'],
    hidden: ['readiness','hotspots','accessibility'],
    banner: 'Emergency Active — Showing critical tools only.',
  },
  recovery: {
    label: 'Recovery',
    color: '#7c3aed',
    emoji: '🟣',
    primary: ['gas','resources','neighborhood','shelters'],
    secondary: ['feed','news','map'],
    hidden: [],
    banner: 'Recovery Mode — Showing restoration resources.',
  },
};

export function useSituation(powerOutagePercent) {
  const nwsAlerts = useAlertStore(s => s.nwsAlerts);

  return useMemo(() => {
    const active = nwsAlerts.filter(a => a.status === 'active');
    const hasWarning = active.some(a => {
      const ev = a.event ?? a.title ?? '';
      return WARNING_EVENTS.some(w => ev.includes(w)) || a.severity === 'Extreme';
    });
    const hasWatch = active.some(a => {
      const ev = a.event ?? a.title ?? '';
      return WATCH_EVENTS.some(w => ev.includes(w)) || a.severity === 'Severe';
    });
    const hasOutages = (powerOutagePercent ?? 0) > 1;
    const level = hasWarning ? 'warning'
      : hasWatch ? 'watch'
      : (active.length === 0 && hasOutages) ? 'recovery'
      : 'normal';
    return { level, ...SITUATION_TOOLS[level] };
  }, [nwsAlerts, powerOutagePercent]);
}
